import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Button, Dropdown, Input, Modal } from '@components';
import CalendarInput from '@components/calendar-input/CalendarInput';
import { IST_TIMEZONE, TimeZones } from '@constants/date';
import { ErrorCode } from '@constants/errorCodes';
import {
  SubscriptionPlanExpiredMessage,
  SubscriptionStatus
} from '@constants/generic';
import UserPermissionTypes from '@constants/permissions';
import { GROUPS, SUBSCRIPTIONS } from '@constants/routes';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import usePerSplitAmountMin from '@features/branch-management/hooks/usePerSplitAmountMin';
import {
  AmountMode,
  amountModeOptions,
  AmountType
} from '@features/transactions/constants';
import { getModalTitleAndSubText } from '@features/transactions/helper';
import {
  clearPartialSplitErrors,
  getPartialSplitAmountErrors,
  getPartialSplitErrorMessage,
  hasPartialSplitError
} from '@features/transactions/helpers/markAsPaid';
import useMarkAsPaidBankAccount from '@features/transactions/hooks/useMarkAsPaidBankAccount';
import { useMarkAsPaidMutation } from '@features/transactions/queires/TransactionsQuery';
import useNotify from '@hooks/notify';
import { ApiError, MarkAsPaidModalProps, ValidationError } from '@types';
import { formatISODate, getFormattedDateTimeWithOffset } from '@utils/date';

import PaymentSuccess from '../payment-success/PaymentSuccess';
import AmountTypeSelector from './AmountTypeSelector';
import BankAccountDropdown from './BankAccountDropdown';
import MarkAsPaidConfirmationModal from './MarkAsPaidConfirmationModal';
import MarkAsPaidFieldRow from './MarkAsPaidFieldRow';

const paymentMethodOptions = amountModeOptions.map((option, index) => ({
  id: index + 1,
  label: option.label,
  value: option.value
}));

const MarkAsPaidModal = ({
  isOpen,
  handleCancel,
  transaction,
  onSuccessUpdate,
  onMarkAsPaidSuccess,
  branchId,
  organizationId,
  flow,
  handleViewDetails,
  onSplitPaymentOrderIdChange,
  onCustomMarkAsPaid,
  showFullAmountOnly = false,
  onModalViewed,
  onConfirmed,
  onProceeded,
  onCancelled,
  onProceedCancelled
}: MarkAsPaidModalProps) => {
  const [amountType, setAmountType] = useState<AmountType>(AmountType.FULL);
  const [amount, setAmount] = useState<string>('0');
  const [amountMode, setAmountMode] = useState<AmountMode>(AmountMode.CASH);
  const [paymentDate, setPaymentDate] = useState<string>(
    getFormattedDateTimeWithOffset(new Date(), TimeZones['Asia/Kolkata'])
  );

  const [note, setNote] = useState<string>('');
  const [validationError, setValidationError] = useState<ValidationError>({});
  const [paidAmount, setPaidAmount] = useState<number | null>(null);
  const [isConformationModalOpen, setIsConformationModalOpen] =
    useState<boolean>(false);

  const [markAsPaid, { isLoading: isLoadingMarkAsPaid }] =
    useMarkAsPaidMutation();
  const { createErrorAlert, createSuccessAlert } = useNotify();
  const { rolesAndPermissions } = useSelector((state: any) => state.user);
  const { currentSubscription } = useSelector((state: any) => state.user);

  const navigate = useNavigate();

  const {
    showBankAccountDropdown,
    availableAccounts,
    preferredAccount,
    handleAccountSelect
  } = useMarkAsPaidBankAccount({ organizationId, branchId, amountMode });

  const minAmount = usePerSplitAmountMin(organizationId, branchId);
  const pendingAmount = Number(
    (Number(transaction.amount) - Number(transaction.paid_amount)).toFixed(2)
  );

  const shouldValidatePartialSplit =
    transaction.is_split_payment &&
    pendingAmount > minAmount &&
    amountType === AmountType.PARTIAL;

  const getPartialErrors = (value: string): ValidationError =>
    shouldValidatePartialSplit
      ? getPartialSplitAmountErrors(value, minAmount, pendingAmount)
      : {};

  // Returns true when the entered partial amount is invalid and errors were set
  const applyPartialSplitErrors = () => {
    const partialErrors = getPartialErrors(amount);
    if (!hasPartialSplitError(partialErrors)) return false;
    setValidationError((prev) => ({ ...prev, ...partialErrors }));
    return true;
  };

  const amountPaidType =
    amountType === AmountType.FULL ? 'full_amount' : 'entered_amount';

  const getAmountToBePaid = () => {
    if (amountType === AmountType.FULL) {
      return pendingAmount;
    }
    return amount;
  };

  const handleMarkAsPaid = async () => {
    if (currentSubscription?.status === SubscriptionStatus.EXPIRED) {
      createErrorAlert(SubscriptionPlanExpiredMessage);
      if (
        rolesAndPermissions.permissions.includes(
          UserPermissionTypes.SUBSCRIPTION_VIEW
        )
      ) {
        navigate(SUBSCRIPTIONS.PLAN_DETAILS);
      }
    } else {
      const amountPaid = Number(getAmountToBePaid());
      const markAsPaidBody = {
        payment_order_id: transaction.id,
        amount: getAmountToBePaid(),
        payment_method: amountMode,
        payment_description: note,
        paid_date: paymentDate,
        ...(showBankAccountDropdown && {
          settlement_account_id: preferredAccount?.accountDetailId
        })
      };

      const resp = onCustomMarkAsPaid
        ? await onCustomMarkAsPaid(markAsPaidBody)
        : await markAsPaid({
            OrgAndBranchId: { organizationId, branchId },
            body: markAsPaidBody
          });

      if ('data' in resp) {
        createSuccessAlert('Payment marked as paid successfully');
        onMarkAsPaidSuccess?.(amountPaid);
        onSuccessUpdate();
        setPaidAmount(amountPaid);
        onConfirmed?.({
          status: 'success',
          amountPaidType,
          amountPaid,
          paymentMode: amountMode,
          paymentDate,
          note
        });

        onSplitPaymentOrderIdChange?.({
          splitPaymentOrderId: resp.data?.split_payment_order_id,
          paymentOrderId: resp.data?.payment_order_id
        });
      } else if ('error' in resp) {
        const error = resp.error as ApiError;
        createErrorAlert(error.data?.message);
        onConfirmed?.({
          status: 'failure',
          failureReason: error.data?.message,
          amountPaidType,
          amountPaid,
          paymentMode: amountMode,
          paymentDate,
          note
        });
        if (error?.data?.errorCode === ErrorCode.ERR_SUBSCRIPTION_EXPIRED) {
          if (
            rolesAndPermissions.permissions.includes(
              UserPermissionTypes.SUBSCRIPTION_VIEW
            )
          ) {
            navigate(SUBSCRIPTIONS.PLAN_DETAILS);
          } else if (flow === 'groupLevel') {
            navigate(GROUPS.HOME);
          }
        }
      }
      setIsConformationModalOpen(false);
    }
  };

  const handleConfirm = async () => {
    sendMixpanelEvent(Event.TRANSACTION_MARK_AS_PAID_MODAL_CONFIRM_CLICKED, {
      paymentMethod: amountMode,
      paymentDescription: note,
      paid_date: formatISODate({
        date: paymentDate,
        format: 'DD MMM YYYY',
        timeZone: IST_TIMEZONE
      }),
      recorded_on: formatISODate({
        date: new Date().toISOString(),
        format: 'DD MMM YYYY at hh:mm am/pm',
        timeZone: IST_TIMEZONE
      })
    });
    if (applyPartialSplitErrors()) {
      setIsConformationModalOpen(false);
      return;
    }
    handleMarkAsPaid();
  };

  const hasDecimal = pendingAmount % 1 !== 0;

  const handlePaymentMethodChange = (selectedValue: string) => {
    setAmountMode(selectedValue as AmountMode);
  };

  const handlePaymentDateChange = (date?: Date) => {
    if (date) {
      setPaymentDate(
        getFormattedDateTimeWithOffset(date, TimeZones['Asia/Kolkata'])
      );
    }
  };

  const handleConfirmModal = () => {
    if (applyPartialSplitErrors()) return;
    setValidationError(clearPartialSplitErrors);
    onProceeded?.({
      amountPaidType,
      amountPaid: Number(getAmountToBePaid()),
      paymentMode: amountMode,
      paymentDate,
      note
    });
    setIsConformationModalOpen(true);
  };

  const handleCancelModal = () => {
    onProceedCancelled?.();
    setIsConformationModalOpen(false);
  };

  const handleFormModalCancel = () => {
    if (!paidAmount) {
      onCancelled?.();
    }
    handleCancel();
  };

  const handleAmountTypeChange = (nextType: AmountType) => {
    setAmountType(nextType);
    if (nextType === AmountType.FULL) {
      setValidationError(clearPartialSplitErrors);
    }
  };

  const handleAmountChange = (value: string) => {
    setAmount(value);
    setValidationError((prev) => ({
      ...clearPartialSplitErrors(prev),
      ...getPartialErrors(value)
    }));
  };

  const hasFiredModalViewedRef = useRef(false);
  useEffect(() => {
    if (!hasFiredModalViewedRef.current) {
      hasFiredModalViewedRef.current = true;
      onModalViewed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showFullAmountOnly) {
      setAmount(String(pendingAmount));
      setAmountType(AmountType.FULL);
      return;
    }

    if (!transaction.is_split_payment || pendingAmount <= minAmount) {
      setAmount(String(pendingAmount));
      setAmountType(AmountType.FULL);
    } else {
      setAmount('');
      setAmountType(AmountType.PARTIAL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showFullAmountOnly,
    transaction.is_split_payment,
    pendingAmount,
    minAmount
  ]);

  return (
    <>
      {!isConformationModalOpen && (
        <Modal
          isOpen={isOpen}
          onCancel={handleFormModalCancel}
          isCloseIconRequired
        >
          <div className="flex w-118 flex-col gap-4 rounded-lg bg-white p-6">
            {paidAmount ? (
              <PaymentSuccess
                amount={paidAmount}
                handleCancel={handleCancel}
                handleViewDetails={handleViewDetails}
              />
            ) : (
              <>
                <div className="flex flex-col gap-2">
                  <div className="text-xl font-medium text-content">
                    {
                      getModalTitleAndSubText(
                        minAmount,
                        pendingAmount,
                        transaction
                      )?.title
                    }
                  </div>
                  <div className="text-sm text-grey">
                    Enter payment details to mark this transaction as paid.
                  </div>
                </div>

                {(showFullAmountOnly ||
                  (transaction.is_split_payment &&
                    pendingAmount > minAmount)) && (
                  <AmountTypeSelector
                    amountType={amountType}
                    onAmountTypeChange={handleAmountTypeChange}
                    amount={amount}
                    onAmountChange={handleAmountChange}
                    errorMessage={getPartialSplitErrorMessage(validationError)}
                    allowDecimals={hasDecimal}
                    showFullAmountOnly={showFullAmountOnly}
                  />
                )}

                <MarkAsPaidFieldRow label="Payment Mode" required>
                  <Dropdown
                    currentFilter={amountMode}
                    filterOptions={paymentMethodOptions}
                    onSelectFilter={handlePaymentMethodChange}
                    placeholder="Select payment method"
                    className="w-63"
                    buttonClassName="!w-full !min-w-full !bg-white !border-greyStroke !rounded-xl !h-14"
                    placeholderStyle="!text-grey !font-normal"
                    listId="payment-method"
                    optionsStyle="!w-full !left-0"
                  />
                </MarkAsPaidFieldRow>
                {showBankAccountDropdown && (
                  <MarkAsPaidFieldRow label="Bank Account">
                    <BankAccountDropdown
                      accounts={availableAccounts}
                      selectedAccountId={preferredAccount?.accountDetailId}
                      onSelect={handleAccountSelect}
                    />
                  </MarkAsPaidFieldRow>
                )}

                <MarkAsPaidFieldRow label="Payment Date" required>
                  <CalendarInput
                    selectedDate={new Date(paymentDate)}
                    handleDateChange={handlePaymentDateChange}
                    className="!w-63 !bg-white"
                    labelStyle="hidden"
                    inputStyle="!bg-white !text-content !border-greyStroke"
                    isMaxDateRequired
                    maxDate={new Date()}
                    label="hidden"
                    modalTitle=""
                    modalTitleStyle="!mt-0"
                  />
                </MarkAsPaidFieldRow>

                <Input
                  placeholder="Notes"
                  value={note}
                  handleInputChange={(value) => setNote(value)}
                  maxLength={100}
                  inputStyle="!text-content"
                />

                <Button
                  label="Confirm"
                  handleClick={handleConfirmModal}
                  disabled={
                    (!showFullAmountOnly &&
                      transaction.is_split_payment &&
                      pendingAmount > minAmount &&
                      amountType === AmountType.PARTIAL &&
                      (amount === '' || Number(amount) === 0)) ||
                    hasPartialSplitError(validationError)
                  }
                />
              </>
            )}
          </div>
        </Modal>
      )}
      {isConformationModalOpen && (
        <MarkAsPaidConfirmationModal
          amount={getAmountToBePaid()}
          payerName={transaction.name}
          isLoading={isLoadingMarkAsPaid}
          onConfirm={handleConfirm}
          onCancel={handleCancelModal}
        />
      )}
    </>
  );
};

export default MarkAsPaidModal;
